import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
    syncNotifications,
    getLocalNotifications,
    getLocalUnreadCount,
    saveLocalNotifications,
    addLocalNotification,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
    clearLocalNotifications
} from '../services/notificationsService';
import { getStoredUser } from './AuthContext';

const NotificationsContext = createContext();

// Polling interval for sync (5 minutes)
const SYNC_INTERVAL = 5 * 60 * 1000;

export const NotificationsProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [synced, setSynced] = useState(false);
    const syncIntervalRef = useRef(null);

    // Get user ID (numeric) from stored user
    const getUserId = useCallback(() => {
        const user = getStoredUser();
        return user?.id || user?.ID || null;
    }, []);

    // Load notifications from localStorage
    const loadLocalNotifications = useCallback(() => {
        const local = getLocalNotifications();
        setNotifications(local);
        setUnreadCount(getLocalUnreadCount());
        return local;
    }, []);

    // Sync notifications with server (at login and periodically)
    const sync = useCallback(async () => {
        const peopleId = getUserId();
        if (!peopleId) return [];

        setLoading(true);
        try {
            const synced = await syncNotifications(peopleId);
            setNotifications(synced);
            setUnreadCount(getLocalUnreadCount());
            setSynced(true);
            return synced;
        } catch (error) {
            console.error('Error syncing notifications:', error);
            return loadLocalNotifications();
        } finally {
            setLoading(false);
        }
    }, [getUserId, loadLocalNotifications]);

    // Mark a notification as read
    const markNotificationAsRead = useCallback(async (notificationId) => {
        const peopleId = getUserId();
        if (!peopleId) return false;

        // Optimistic update
        setNotifications(prev =>
            prev.map(n =>
                (n.id || n.ID) === notificationId
                    ? { ...n, isRead: true, ISREAD: true, readAt: new Date().toISOString() }
                    : n
            )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));

        const result = await markAsRead(notificationId, peopleId);
        const success = result.success ?? result.SUCCESS;

        if (!success) {
            // Rollback on failure
            loadLocalNotifications();
        }

        return success;
    }, [getUserId, loadLocalNotifications]);

    // Mark a notification as unread
    const markNotificationAsUnread = useCallback(async (notificationId) => {
        const peopleId = getUserId();
        if (!peopleId) return false;

        // Optimistic update
        setNotifications(prev =>
            prev.map(n =>
                (n.id || n.ID) === notificationId
                    ? { ...n, isRead: false, ISREAD: false, readAt: null, READAT: null }
                    : n
            )
        );
        setUnreadCount(prev => prev + 1);

        const result = await markAsUnread(notificationId, peopleId);
        const success = result.success ?? result.SUCCESS;

        if (!success) {
            // Rollback on failure
            loadLocalNotifications();
        }

        return success;
    }, [getUserId, loadLocalNotifications]);

    // Mark all notifications as read
    const markAllNotificationsAsRead = useCallback(async () => {
        const peopleId = getUserId();
        if (!peopleId) return false;

        // Optimistic update
        setNotifications(prev =>
            prev.map(n => ({ ...n, isRead: true, ISREAD: true, readAt: new Date().toISOString() }))
        );
        setUnreadCount(0);

        const result = await markAllAsRead(peopleId);
        const success = result.success ?? result.SUCCESS;

        if (!success) {
            // Rollback on failure
            loadLocalNotifications();
        }

        return success;
    }, [getUserId, loadLocalNotifications]);

    // Delete a notification
    const removeNotification = useCallback(async (notificationId) => {
        const peopleId = getUserId();
        if (!peopleId) return false;

        // Optimistic update
        setNotifications(prev => prev.filter(n => (n.id || n.ID) !== notificationId));
        setUnreadCount(getLocalUnreadCount());

        const result = await deleteNotification(notificationId, peopleId);
        const success = result.success ?? result.SUCCESS;

        if (!success) {
            // Rollback on failure
            loadLocalNotifications();
        }

        return success;
    }, [getUserId, loadLocalNotifications]);

    // Add a new notification locally (for real-time updates via WebSocket)
    const addNotification = useCallback((notification) => {
        addLocalNotification(notification);
        setNotifications(prev => [notification, ...prev]);
        setUnreadCount(prev => prev + 1);
    }, []);

    // Refresh = sync with server
    const refresh = useCallback(async () => {
        return sync();
    }, [sync]);

    // Clear all notifications (logout)
    const clear = useCallback(() => {
        clearLocalNotifications();
        setNotifications([]);
        setUnreadCount(0);
        setSynced(false);
    }, []);

    // Initial load from localStorage + sync
    useEffect(() => {
        const peopleId = getUserId();

        // Load from localStorage immediately
        loadLocalNotifications();

        if (peopleId) {
            // Then sync with server
            sync();

            // Set up periodic sync
            syncIntervalRef.current = setInterval(() => {
                sync();
            }, SYNC_INTERVAL);
        }

        return () => {
            if (syncIntervalRef.current) {
                clearInterval(syncIntervalRef.current);
            }
        };
    }, [getUserId, loadLocalNotifications, sync]);

    // Listen for custom notification events (for real-time updates from WebSocket)
    useEffect(() => {
        const handleNewNotification = (event) => {
            if (event.detail) {
                addNotification(event.detail);
            }
        };

        window.addEventListener('newNotification', handleNewNotification);
        return () => window.removeEventListener('newNotification', handleNewNotification);
    }, [addNotification]);

    // Listen for login event to trigger sync
    useEffect(() => {
        const handleLogin = () => {
            sync();
        };

        window.addEventListener('userLoggedIn', handleLogin);
        return () => window.removeEventListener('userLoggedIn', handleLogin);
    }, [sync]);

    // Listen for logout event to clear notifications
    useEffect(() => {
        const handleLogout = () => {
            clear();
        };

        window.addEventListener('userLoggedOut', handleLogout);
        return () => window.removeEventListener('userLoggedOut', handleLogout);
    }, [clear]);

    const value = {
        notifications,
        unreadCount,
        loading,
        synced,
        sync,
        refresh,
        markNotificationAsRead,
        markNotificationAsUnread,
        markAllNotificationsAsRead,
        removeNotification,
        addNotification,
        clear
    };

    return (
        <NotificationsContext.Provider value={value}>
            {children}
        </NotificationsContext.Provider>
    );
};

export const useNotifications = () => {
    const context = useContext(NotificationsContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationsProvider');
    }
    return context;
};

export default NotificationsContext;
